<template>
  <div v-if="!this.isAdmin">
    <div class="dialog-container dialog-light">
      <div class="dialog" :class="{ 'dialog-md': !selectingPlan, 'dialog-lg': selectingPlan }" v-if="loaded">
        <ax-dialog-header :text="subscriptionModalHeaderText" :type="'subscription'"></ax-dialog-header>
        <p v-if="activeSubscription?.isTrial" class="text-center fst-italic">{{ this.trialCountdown }}</p>
        <div class="dialog-body">

          <!-- Failsafe, in case an invalid user gets to this screen -->
          <div v-if="!isOwner" class="subscription-form">
            <div class="form-group">
              <p class="mb-0">You cannot view or edit this organsation's subscription information. Please contact your organisation's owner.</p>
            </div>
            <div class="mt-5">
              <k-button class="btn btn-lg btn-outline-primary w-100" type="button" @click="close">Back</k-button>
            </div>
          </div>

          <!-- Normal subscription popup -->
          <div v-else class="subscription-form">

            <!-- Billing details -->
            <div v-if="!(selectingPlan || enteringPaymentDetails)">
              <h2 class="mt-2 mb-0" v-if="!activeSubscription?.isFreeAccount">Billing Details</h2>
              <div class="form-group">
                <p class="mb-0">{{ billingName }}</p>
                <p class="mb-0">{{ billingEmail }}</p>
                <p>{{ billingPhone }}</p>
                <a v-if="hasStripeDetails" class="btn btn-sm btn-primary mb-2" @click="edit">Update</a>
              </div>
              <div class="text-spacer no-pad">
                <span class="line long"></span>
              </div>
            </div>

            <div>
              
              <!-- Free Account -->
              <div v-if="activeSubscription?.isFreeAccount">
                <h2 class="mb-0">Free Subscription</h2>
                <p>You have been granted a free subscription to Sinchii. No payment is required</p>
                <div class="mt-5">
                  <k-button class="btn btn-lg btn-outline-primary w-100" type="button" @click="close">Close</k-button>
                </div>
              </div>

              <!-- Stripe subscription (new) / Not Subscribed -->
              <div v-else-if="selectingPlan">

                <subscription-option v-for="option in subscriptionOptions"
                                     :key="option.subscriptionOptionID"
                                     :subscription-option="option"
                                     @onSelect="trySelectSubscriptionOption"
                                     :selected="activeSubscription?.subscriptionOptionID == option.subscriptionOptionID" />

                <k-button class="btn btn-lg btn-outline-primary w-100 mt-5" type="button" @click="closeSubscriptionChange">Back</k-button>
              </div>

              <div v-else-if="enteringPaymentDetails">
                <div class="bubble white large mb-4 selected">
                  <div class="inner full">
                    <div>
                      <h2 class="mb-2 span">{{ selectedSubscriptionOption.displayName }}</h2>
                      <span class="sale-badge" v-if="selectedSubscriptionOption?.discountPercentage">{{ (selectedSubscriptionOption.discountPercentage * 100) }}% off</span>
                      <span class="sale-badge" v-if="selectedSubscriptionOption?.discountAmount && selectedSubscriptionOption.isUsageBased">Save {{ (formatSubscriptionCost(selectedSubscriptionOption.discountAmount)) }} off each bill</span>
                      <span class="sale-badge" v-if="selectedSubscriptionOption?.discountAmount && !selectedSubscriptionOption.isUsageBased">{{ (formatSubscriptionCost(selectedSubscriptionOption.discountAmount)) }} off</span>

                      <h2 class="mb-0">
                        {{ formatSubscriptionCost(selectedSubscriptionOption.totalCost) }}/{{ (selectedSubscriptionOption?.isMonthly ? "month" : "year") }}
                        {{ (selectedSubscriptionOption?.isUsageBased ? " (per org.)" : "") }}
                        {{ (subscriptionChanges ? "*" : "") }}
                      </h2>
                      <p class="pt9-em mb-0">Price includes GST</p>

                      <p class="disclaimer mb-0 mt-3" v-if="subscriptionChanges">
                        * {{ formatSubscriptionCost(selectedSubscriptionOption?.totalCost) }}/{{ (selectedSubscriptionOption?.isMonthly ? "month" : "year") }} {{ (selectedSubscriptionOption?.isUsageBased ? " (per org.)" : "") }}
                        {{ disclaimerText }}
                        {{ formatSubscriptionCost(selectedSubscriptionOption?.nextBaseCost) }}/{{ (selectedSubscriptionOption?.nextIsMonthly ? "month" : "year") }} {{ (selectedSubscriptionOption?.nextIsUsageBased ? " (per org.)" : "") }}
                      </p>
                    </div>
                    <div class="icon">
                      <i class="icon-check"></i>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <!-- Not Subscribed / Trial -->
                  <p v-if="!activeSubscription || activeSubscription.isTrial">
                    <!-- Recurring Plan (static pricing) -->
                    <span v-if="!selectedSubscriptionOption?.isUsageBased">
                      Your plan will start immediately. Your subscription will auto-renew {{(selectedSubscriptionOption?.isMonthly ? "monthly" : "annually")}} until the organisation's subscription is cancelled.
                    </span>
  
                    <!-- Usage Based Plan -->
                    <span v-else>
                      Your plan will start immediately. Your subscription will auto-renew {{(selectedSubscriptionOption?.isMonthly ? "monthly" : "annually")}} until your subscription is cancelled. You will be charged based on the number of active organisations you have used throughout the {{(selectedSubscriptionOption?.isMonthly ? "month" : "year")}}.
                    </span>
                  </p>

                  <p v-else>
                    <!-- Recurring Plan (static pricing) -->
                    <span v-if="!selectedSubscriptionOption?.isUsageBased">
                      Your plan will change on {{ activeSubscription.periodEnd }} at the end of your current billing cycle. Your subscription will auto-renew {{(selectedSubscriptionOption?.isMonthly ? "monthly" : "annually")}} until the organisation's subscription is cancelled.
                    </span>
  
                    <!-- Usage Based Plan -->
                    <span v-else>
                      Your plan will change on {{ activeSubscription.periodEnd }} at the end of your current billing cycle. Your subscription will auto-renew {{(selectedSubscriptionOption?.isMonthly ? "monthly" : "annually")}} until the organisation's subscription is cancelled. You will be charged based on the number of active organisations you have used throughout the {{(selectedSubscriptionOption?.isMonthly ? "month" : "year")}}.
                    </span>
                  </p>
                </div>

                <!-- First time subscriptions only - Show the Stripe card entry -->
                <div class="form-group" v-if="!hasStripeDetails">
                  <StripeElements
                    v-if="stripeLoaded"
                    v-slot="{ elements }"
                    ref="elms"
                    :stripe-key="stripeKey"
                    :instance-options="instanceOptions"
                    :elements-options="elementsOptions"
                  >
                    <StripeElement
                      ref="card"
                      :elements="elements"
                      :options="cardOptions"
                    />
                  </StripeElements>
                </div>

                <!-- For users with a payment method -->
                <k-button
                  class="btn btn-secondary btn-lg w-100 mb-2 mt-4"
                  type="button"
                  :disabled="paying"
                  @click="pay"
                >
                  {{ (!activeSubscription ? "Confirm payment" : "Confirm change") }}
                </k-button>

                <k-button class="mt-4 btn btn-lg btn-outline-primary w-100" type="button" @click="closePaymentDetails">Back</k-button>
              </div>

              <!-- New Subscription / Not subscribed -->
              <div class="form-group" v-else-if="!activeSubscription || activeSubscription.isTrial">
                <p class="mt-4">You are not subscribed to Sinchii. {{ activeSubscription && activeSubscription.isTrial ? "Subscribe to retain access after your trial period ends" : "Subscribe now to start forecasting smarter, faster." }}</p>

                <!-- Stripe -->
                <k-button
                  class="btn btn-secondary btn-lg w-100 mb-2"
                  type="button"
                  :disabled="paying"
                  @click="showSubscriptionChange"
                >
                  Subscribe now
                </k-button>

                <div class="mt-5">
                  <k-button class="btn btn-lg btn-outline-primary w-100" type="button" @click="close">Close</k-button>
                </div>
              </div>
              
              <!-- Active Susbcription -->
              <div v-else-if="!!activeSubscription && !activeSubscription.isTrial">
                <h2 class="mb-1 mt-4">
                  {{(nextSubscription?.isMonthly ?? activeSubscription?.isMonthly ? "Monthly" : "Annual")}} Subscription: {{ formatSubscriptionCost(nextSubscription?.totalCost ?? activeSubscription?.totalCost) }}
                  <span v-if="!(nextSubscription?.isUsageBased ?? activeSubscription?.isUsageBased)">/{{ (nextSubscription?.isMonthly ?? activeSubscription?.isMonthly ? "month" : "year") }}</span>
                  <span v-else> *</span>
                </h2>

                <p v-if="activeSubscription?.isUsageBased" class="mb-0">
                  {{ formatSubscriptionCost(activeSubscription?.baseCost) }} per org. ({{ activeSubscription.usageForPeriod ?? "[ERR]" }} orgs. for current period) 
                  <span v-if="activeSubscription?.discountPercentage"> with a {{ (activeSubscription?.discountPercentage * 100) }}% discount</span>
                  <span v-if="activeSubscription?.discountAmount && activeSubscription?.isUsageBased"> with {{ (formatSubscriptionCost(activeSubscription?.discountAmount)) }} discount.</span>
                </p>

                <p>Price includes GST</p>

                <!-- Stripe -->
                <div class="form-group">
                  <div class="flex-space mb-4">
                    <!-- Change Subscription -->
                    <k-button
                      v-if="subscriptionOptions?.length > 0"
                      class="btn btn-sm expand btn-primary"
                      type="button"
                      @click="showSubscriptionChange()"
                    >
                      Change subscription
                    </k-button>

                    <!-- Cancel Subscription -->
                    <k-button
                      v-if="this.isSubscribed && !this.activeSubscription?.isTrial && !this.isActuallyCancelled"
                      class="btn btn-sm expand btn-outline-primary"
                      type="button"
                      @click="showCancelSubModal()"
                    >
                      Cancel subscription
                    </k-button>
                  </div>
  
                  <!-- Subscribed Account -->
                  <p v-if="!isActuallyCancelled && isSubscribed">

                    <!-- Plan changing (static pricing) -->
                    <span v-if="subscriptionChanging" class="mt-3">
                      Your plan is active. <b>On {{activeSubscription.periodEnd}} your subscription will change to a new {{(nextSubscription.isMonthly ? "monthly" : "annual")}} plan</b>. 
                      {{ (nextSubscription.isUsageBased ? "You will be charged" : "Your new subscription cost will be") }} {{formatSubscriptionCost(nextSubscription?.totalCost)}} {{ (nextSubscription.isUsageBased ? "per organisation used during this period" : "") }}.
                      Your subscription will auto-renew {{(nextSubscription.isMonthly ? "monthly" : "annually")}} until the organisation's subscription is cancelled.
                    </span>
  
                    <!-- Recurring Plan (static pricing) -->
                    <span v-else-if="!activeSubscription?.isUsageBased">
                      Your plan is active. Your subscription will renew on {{ activeSubscription.periodEnd }} with ongoing payments each {{ (activeSubscription.isMonthly ? "month" : "year") }} after.
                      Your subscription auto-renews {{(activeSubscription?.isMonthly ? "monthly" : "annually")}} until your subscription is cancelled.
                    </span>
  
                    <!-- Usage Based Plan -->
                    <span v-else>
                      Your plan is active. Your subscription will renew on {{ activeSubscription.periodEnd }} with ongoing payments each {{ (activeSubscription.isMonthly ? "month" : "year") }} after.
                      Your subscription auto-renews {{(activeSubscription?.isMonthly ? "monthly" : "annually")}} until your subscription is cancelled. You will be charged based on the number of active organisations you have used throughout the {{(activeSubscription.isMonthly ? "month" : "year")}}.
                    </span>
                  </p>
  
                  <!-- Cancelled Subscription -->
                  <p v-else-if="this.isActuallyCancelled && this.isSubscribed">
                    Your plan has been cancelled. You can keep on using your organisation{{ (activeSubscription?.isUsageBased ? "s" : "") }} until your subscription expires on {{ activeSubscription.periodEnd }}. Re-subscribe to keep access after this period. {{ (activeSubscription?.isUsageBased ? "You will be billed for the active organisations you use through the remainder of your subscription period." : "") }}
                  </p>

                  <p v-if="activeSubscription?.discountExpiring">
                    <span v-if="isActuallyCancelled">
                      A discount previously applied to your subscription has now ended. If reactivating your subscription it will now reflect the standard subscription price.
                    </span>
                    <span v-else>
                      A discount previously applied to your subscription has now ended. Your next invoice (shown above) will now reflect the standard subscription price.
                    </span>
                  </p>
                  <p v-else-if="activeSubscription?.discountAmount">
                    <span>
                      <b>Your subscription currently has a discount of {{ (formatSubscriptionCost(activeSubscription?.discountAmount)) }} applied to it.</b>
                    </span>
                  </p>
                  <p v-else-if="activeSubscription?.discountPercentage">
                    <span>
                      <b>Your subscription currently has a discount of {{ (activeSubscription?.discountPercentage * 100) }}% applied to it.</b>
                    </span>
                  </p>
                </div>

                <!-- Action Buttons -->
                <div class="mt-5 mb-3">
                  <div v-if="buttonConfig">
                    <k-button
                      :class="buttonConfig.class"
                      class="btn btn-lg w-100 mb-2"
                      type="button"
                      @click="buttonConfig.action"
                      :disabled="buttonConfig.disabled"
                      v-if="buttonConfig.show"
                    >
                      {{ buttonConfig.text }}
                    </k-button>
                  </div>
                </div>
                <div class="mt-3">
                  <k-button class="btn btn-lg btn-outline-primary w-100" type="button" @click="close">Close</k-button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
 </div>
 <div v-else>
    <div class="container header">
      <ax-dialog-header text="Subscription" :type="'subscription'"></ax-dialog-header>
      <label class="switch">
        <k-checkbox
          ref="freeAccount"
          label="Allow Free Account"              
          v-model="checked"
          size="large"
          class="white-checkbox"
        />
      </label>
      <div class="mt-3">
        <k-button class="btn btn-lg btn-secondary w-100 save mb-2" type="button" @click="submitFreeAccount">Confirm</k-button>
        <k-button class="btn btn-lg btn-outline-primary w-100" type="button" @click="close">Back</k-button>
      </div>
    </div>
  </div>
</template>

<script>
  import StripeService from "../../services/stripe-subscription-service";
  import SubscriptionService from "../../services/subscription-service";
  import store from "../../store/index";
  import { loadStripe } from "@stripe/stripe-js";
  import { StripeElements, StripeElement } from "vue-stripe-js";
  import icon from "../Icon.vue";
  import { ref } from "@vue/reactivity";
  import { onBeforeMount } from "vue"
  import { useToast } from "vue-toastification";
  import SubscriptionOption from "../SubscriptionOption.vue";

  const toast = useToast();

  export default {
    components: {
      StripeElements,
      StripeElement,
      icon,
      "subscription-option": SubscriptionOption,
    },
    props: {
      businessID: {
        default: null,
        type: Number
      }
    },
    setup() {
      const stripeKey = ref(store.getters.stripePublishableKey)
      const instanceOptions = ref({
        // https://stripe.com/docs/js/initializing#init_stripe_js-options
        locale: "auto"
      })
      const elementsOptions = ref({
        // https://stripe.com/docs/js/elements_object/create#stripe_elements-options
        locale: "auto",
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#0570de",
            colorBackground: "#ffffff",
            colorText: "green",
            colorDanger: "#df1b41",
            fontFamily: "Ideal Sans, system-ui, sans-serif",
            spacingUnit: "2px",
            borderRadius: "4px"
            // See all possible variables below
          }
        }
      })
      const cardOptions = ref({
        // https://stripe.com/docs/stripe.js#element-options
        value: {
          postalCode: "12345"
        }
      })
      const stripeLoaded = ref(false)
      const card = ref()
      const elms = ref()

      onBeforeMount(async () => {
        const stripePublishableKey = store.getters.stripePublishableKey;
        if (!stripePublishableKey) { // make sure we have the stripe key
          await StripeService.getStripeConfig()
        }
        const stripePromise = loadStripe(store.getters.stripePublishableKey)
        stripePromise.then(() => {
          stripeLoaded.value = true
        })
      })

      return {
        stripeKey,
        stripeLoaded,
        instanceOptions,
        elementsOptions,
        cardOptions,
        card,
        elms
      }
    },
    data() {
      return {
        loaded: false,
        paying: false,
        selectingPlan: false,
        enteringPaymentDetails: false,
        checked: false,
        trialCountdown: null,
        isOwner: false,
        susbcriptionType: "stripe",
        // Billing details
        billingName: "",
        billingEmail: "",
        cardNumber: "",
        headerDescription: "",
        billingPhone: "",
        cardType: "",
        billingCardNumber: "",
        hasStripeDetails: null,
        subscriptionOptions: null,
        selectedSubscriptionOption : {},
        activeSubscription: null,
        nextSubscription: null
      }
    },
    computed: {
      subscriptionChanging() {
        return this.nextSubscription != null && this.activeSubscription?.isCancelled;
      },
      isSubscribed() {
        return this.activeSubscription != null;
      },
      isActuallyCancelled() {
        return this.activeSubscription?.isCancelled && !this.subscriptionChanging;
      },
      subscriptionModalHeaderText() {
        if (this.selectingPlan) {
          return "Select a subscription plan";
        }
        else if (this.enteringPaymentDetails && this.hasStripeDetails) {
          return "Confirm subscription change";
        }
        else if (this.enteringPaymentDetails && !this.hasStripeDetails) {
          return "Enter payment details";
        }
        else {
          return "Subscription";
        }
      },
      buttonConfig() {
        const updateButton = this.cardRemoved && this.hasStripeDetails;
        const reactivateButton = (!!this.activeSubscription && !this.activeSubscription.isTrial) && this.hasStripeDetails && this.isActuallyCancelled;
        
        switch (true) {
          case updateButton:
            return {
              text: "Update Payment Details",
              class: "btn-secondary",
              action: this.edit,
              disabled: false,
              show: true
            }
          case reactivateButton:
            return {
              text: "Reactivate subscription",
              class: "btn-secondary",
              action: () => this.showActivateSubModal(),
              disabled: false,
              show: true
            }
        }

        return null;
      },
      isAdmin() {
        return store.getters.isAdmin;
      },
      subscriptionChanges() {
        return (this.selectedSubscriptionOption?.totalCost != this.selectedSubscriptionOption?.nextBaseCost)
          || (this.selectedSubscriptionOption?.isMonthly != this.selectedSubscriptionOption?.nextIsMonthly)
          || (this.selectedSubscriptionOption?.isUsageBased != this.selectedSubscriptionOption?.nextIsUsageBased)
      },
      disclaimerText() {
        if (this.selectedSubscriptionOption?.discountDuration == 'once') {
          return "includes initial payment only, then";
        }
        else if (this.selectedSubscriptionOption?.discountDuration == 'repeating') {
          return "for the first " + this.selectedSubscriptionOption.discountMonths + " months, then";
        }
        else {
          return "includes initial payment only, then";
        }
      }
    },
    async mounted() {
      this.loaded = false;

      this.$emit("modalSizeChange", "md");

      await this.getSubscriptionInformation();
      await this.getAvailableSubscriptionOptions();

      if (this.activeSubscription?.isFreeAccount) {
          this.loaded = true;
          return;
      }
      
      if (!this.isAdmin) {
          await this.getBillingDetails(this.businessID);

        if (this.activeSubscription?.isTrial) {
          await this.getTrialCountdown();
        }
      }

      this.loaded = true;
    },
    methods: {
      formatSubscriptionCost(price) {
        var currency = "AUD";

        const formatter = new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        const formattedPrice = formatter.format(price);
        return formattedPrice + " " + currency;
      },
      convertToDateString(dateString) {
        const date = new Date(dateString + "Z");
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
      },
      edit() {
        StripeService.customerPortalRedirect()
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .then((response) => {
          location.href = response.redirectUrl;
        }).catch(this.showAlert);
      },
      close() {
        this.$emit("onClose");
      },
      showAlert(error) {
        toast.clear();
        toast.error(error.message);
        this.paying = false; // Failsafe in case of error
      },
      showSuccess(msg) {
        toast.clear();
        toast.success(msg);
      },
      cancelSubscription() {
        StripeService.cancelSubscription(this.businessID)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .then(() => {
            this.showSuccess("Success!");
            store.dispatch("updateSubscriptionState");
            this.$root.showSubscriptionModal(this.businessID); // Re-show the modal
          }).catch(this.showAlert);
      },
      activateSubscription(targetSubscriptionOption) {
        var wasLocked = !this.activeSubscription;

        StripeService.activateSubscription(this.businessID, targetSubscriptionOption?.subscriptionOptionID)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .then(() => {
            this.selectedSubscriptionOption = null;

            this.showSuccess("Success!");
            store.dispatch("updateSubscriptionState");
            this.$root.showSubscriptionModal(this.businessID); // Re-show the modal
            this.closeSubscriptionChange();
            this.paying = false;

            // Ensure we reload the page if the subscription was locked before (to update internal state)
            if (wasLocked) {
              location.reload();
            }
          }).catch(this.showAlert);    
      },
      reactivateSubscription() {
        var wasLocked = !this.activeSubscription;

        StripeService.reactivateSubscription(this.businessID)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .then(() => {
            this.selectedSubscriptionOption = null;

            this.showSuccess("Success!");
            store.dispatch("updateSubscriptionState");
            this.$root.showSubscriptionModal(this.businessID); // Re-show the modal
            this.closeSubscriptionChange();
            this.paying = false;

            // Ensure we reload the page if the subscription was locked before (to update internal state)
            if (wasLocked) {
              location.reload();
            }
          }).catch(this.showAlert);    
      },
      async getBillingDetails() {
        await StripeService.getBillingDetails(this.businessID)
        .then((response) => {
          if (response) {
            this.billingName = response.billingName;
            this.billingEmail = response.billingEmail;
            this.billingPhone = response.billingPhone;
            this.cardType = response.cardType;
            this.billingCardNumber = response.cardNumber;
            this.expiryDate = response.expiryDate;
            this.cardRemoved = !this.cardType && !this.cardNumber && !this.expiryDate;
            this.hasStripeDetails = response.hasStripeDetails;
          }
        })
        .catch(this.showAlert);
      },
      pay() {
        this.paying = true;

        if (this.hasStripeDetails) {
          this.activateSubscription(this.selectedSubscriptionOption);
        }
        else {
          // Get stripe element
          const cardElement = this.card.stripeElement
  
          // Access instance methods, e.g. createToken()
          this.elms.instance.createToken(cardElement).then((result) => {
            // Handle result.error or result.token
            if (result.error) {
              toast.clear();
              toast.error("An error occurred while adding your card - please check your card details");
              this.paying = false;
            } else {
              this.tokenCreated(result.token);
            }
          })
          .catch(() => this.paying = false);
        }
      },
      tokenCreated (token) {
        var wasLocked = !this.activeSubscription;

        StripeService.activateSubscription(
          this.businessID,
          this.selectedSubscriptionOption.subscriptionOptionID,
          {
            token: token,
            billingName: this.billingName,
            email: this.billingEmail,
            phoneNumber: this.billingPhone
          }
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .then(() => {
          this.selectedSubscriptionOption = null;

          this.showSuccess("Success!");
          store.dispatch("updateSubscriptionState");
          this.$root.showSubscriptionModal(this.businessID); // Re-show the modal
          this.closeSubscriptionChange();
          this.paying = false;
          
          // Ensure we reload the page if the subscription was locked before (to update internal state)
          if (wasLocked) {
            location.reload();
          }

          this.close();
        }).catch(this.showAlert);
      },
      async submitFreeAccount() {
        try {
          await SubscriptionService.updateFreeAccountStatus(this.checked, this.businessID);
        }
        catch (ex) {
          this.showAlert(ex)
          return;
        }

        if (this.checked) {
          toast.success("Successful! This organisation (or affiliate) has free access now.")
        }
        else {
          toast.success("Successful! This organisation (or affiliate) no longer has free account access.");
        }

        this.$emit("onClose");
        
        setTimeout(() => {
          this.$router.go();
        }, 2000);
      },
      async getAvailableSubscriptionOptions() {
        try {
          const response = await SubscriptionService.getAvailableSubscriptionOptions(this.businessID);

          if (response?.availableSubscriptionOptions) {
            this.subscriptionOptions = response.availableSubscriptionOptions;
            this.selectedSubscriptionOption = null;
          }
        } catch (error) {
          this.showAlert(error);
        }
      },
      async getSubscriptionInformation() {
        const response = await SubscriptionService.getSubscriptionInformation(this.businessID);

        this.isOwner = response.isOwner;

        if (response?.activeSubscription) {
          this.activeSubscription = {
            isCancelled: response.activeSubscription.isCancelled,
            isTrial: response.activeSubscription.isTrial,
            isFreeAccount: response.activeSubscription.isFreeAccount,
            periodStard: this.convertToDateString(response.activeSubscription.startDate),
            periodEnd: this.convertToDateString(response.activeSubscription.endDate),
            endsIn: response.activeSubscription.trialEndsIn,
            hasPaymentMethod: response.activeSubscription.hasPaymentMethod,
            subscriptionType: response.activeSubscription.subscriptionType,
            isMonthly: response.activeSubscription.isMonthly,
            isUsageBased: response.activeSubscription.isUsageBased,
            usageForPeriod: response.activeSubscription.usageForPeriod,
            totalCost: response.activeSubscription.totalCost,
            baseCost: response.activeSubscription.baseCost,
            discountPercentage: response.activeSubscription.discountPercentage,
            discountAmount: response.activeSubscription.discountAmount,
            discountExpiring: response.activeSubscription.discountExpiring
          }

          this.checked = response.activeSubscription.isFreeAccount;
          this.subscriptionType = response.activeSubscription.subscriptionType;
        }
        else {
          // Fallback for when there is no active subscription
          this.subscriptionType = response.subscriptionType;
        }

        if (response?.nextSubscription) {
          this.nextSubscription = {
            isCancelled: response.nextSubscription.isCancelled,
            isTrial: response.nextSubscription.isTrial,
            isFreeAccount: response.nextSubscription.isFreeAccount,
            periodStard: this.convertToDateString(response.nextSubscription.startDate),
            periodEnd: this.convertToDateString(response.nextSubscription.endDate),
            endsIn: response.nextSubscription.trialEndsIn,
            hasPaymentMethod: response.nextSubscription.hasPaymentMethod,
            subscriptionType: response.nextSubscription.subscriptionType,
            isMonthly: response.nextSubscription.isMonthly,
            isUsageBased: response.nextSubscription.isUsageBased,
            usageForPeriod: response.nextSubscription.usageForPeriod,
            totalCost: response.nextSubscription.totalCost,
            baseCost: response.nextSubscription.baseCost,
            discountPercentage: response.nextSubscription.discountPercentage,
            discountAmount: response.nextSubscription.discountAmount,
            discountExpiring: response.nextSubscription.discountExpiring
          }
        }
      },
      async getTrialCountdown() {
        if (this.activeSubscription?.endsIn) {
          if (this.activeSubscription?.endsIn === "1") {
            this.trialCountdown = "Trial ends in " + this.activeSubscription?.endsIn + " day";
          } else if (this.endsIn === "0") {
            this.trialCountdown = "Trial ends in < 1 day";
          } else {
            this.trialCountdown = "Trial ends in " + this.activeSubscription?.endsIn + " days";
          }
        }
      },
      trySelectSubscriptionOption(option) {
        this.selectedSubscriptionOption = option;
        this.showPaymentDetails();
      },
      showActivateSubModal() {
        const modalContent = {
          title: "Re-activate Subscription",
          description: "Confirm re-activatation of your subscription?",
          closeBttText: "No",
          confirmBttText: "Yes"
        }
        
        this.$root.showConfirmation({
          modalContent: modalContent,
          type: "subscription",
          action: () => this.reactivateSubscription(),
          cancel: () => this.$root.showSubscriptionModal(this.businessID)
        });
      },
      showSubscriptionChange() {
        this.selectingPlan = true;
        this.enteringPaymentDetails = false;
        this.selectedSubscriptionOption = null;
        this.$emit("modalSizeChange", "lg");
      },
      closeSubscriptionChange() {
        this.selectingPlan = false;
        this.enteringPaymentDetails = false;
        this.selectedSubscriptionOption = null;
        this.$emit("modalSizeChange", "md");
      },
      showPaymentDetails() {
        if (this.selectedSubscriptionOption == null || this.hasPaymentMethod) {
          this.closeSubscriptionChange(); // Invalid selection, close the modal
          return;
        }

        this.selectingPlan = false;
        this.enteringPaymentDetails = true;
        this.$emit("modalSizeChange", "md");
      },
      closePaymentDetails() {
        this.selectingPlan = true;
        this.enteringPaymentDetails = false;
        this.$emit("modalSizeChange", "lg");
      },
      showCancelSubModal() {
        const cancelModalContent = {
          title: "Cancel Subscription",
          description: "Are you sure you want to cancel your subscription? You will retain access until the end of the subscription period.",
          closeBttText: "No",
          confirmBttText: "Yes"
        }
        
        this.$root.showConfirmation({
          modalContent: cancelModalContent,
          type: "subscription",
          action: () => this.cancelSubscription(),
          cancel: () => this.$root.showSubscriptionModal(this.businessID)
        });
      },
      showFreeAccountModal() {
        var freeAccountModalContent = {
          title: "Free Account",
          description: "The subscription is already active. Are you sure you want to proceed with the changes?",
          closeBttText: "No",
          confirmBttText: "Yes"
        }

        this.$root.showConfirmation({
          modalContent: freeAccountModalContent,
          type: "subscription",
          action: () => this.submitFreeAccount(),
          cancel: () => this.$root.showSubscriptionModal(this.businessID)
        });
      },
    }
  }
</script>
